/**
 * One job's live state, folded from the event stream.
 *
 * The service replays every stored event before going live, so this hook does not care whether
 * the build started five seconds or five hours ago -- it always ends up with the same picture.
 */
import { useEffect, useRef, useState } from 'react';
import { api, streamJob, type Job, type LiveEvent } from './api';

export interface StepState {
  status: 'ok' | 'fail' | 'skip' | 'running';
  seconds?: number;
  message?: string;
}

export interface LogLine {
  seq: number;
  text: string;
  tone: 'plain' | 'step' | 'err' | 'warn' | 'note';
}

export interface JobStream {
  job: Job | null;
  steps: Record<string, StepState>;
  active: string | null;
  lines: LogLine[];
  warnings: string[];
  refresh: () => void;
}

export function useJobStream(jobId: number | null): JobStream {
  const [job, setJob] = useState<Job | null>(null);
  const [steps, setSteps] = useState<Record<string, StepState>>({});
  const [active, setActive] = useState<string | null>(null);
  const [lines, setLines] = useState<LogLine[]>([]);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [nonce, setNonce] = useState(0);
  const counter = useRef(0);

  useEffect(() => {
    if (jobId === null) {
      setJob(null);
      setSteps({});
      setActive(null);
      setLines([]);
      setWarnings([]);
      return undefined;
    }

    let cancelled = false;
    counter.current = 0;
    setSteps({});
    setActive(null);
    setLines([]);
    setWarnings([]);

    void api.job(jobId).then((value) => {
      if (!cancelled) setJob(value);
    });

    const push = (text: string, tone: LogLine['tone']): void => {
      counter.current += 1;
      const line = { seq: counter.current, text, tone };
      // Older lines are dropped rather than the newest: a long Gradle log must not stall the tab.
      setLines((current) => (current.length > 4000 ? [...current.slice(-3000), line] : [...current, line]));
    };

    const onEvent = (event: LiveEvent): void => {
      const payload = event.payload as Record<string, never> & {
        step?: string; status?: StepState['status']; seconds?: number; message?: string;
        text?: string; stream?: string; kind?: string; avd?: string; port?: number;
      };
      switch (event.type) {
        case 'step.start':
          if (payload.step) {
            setActive(payload.step);
            push(payload.step, 'step');
          }
          break;
        case 'step.end':
          if (payload.step) {
            const status = payload.status ?? 'ok';
            setSteps((current) => ({
              ...current,
              [payload.step as string]: { status, seconds: payload.seconds, message: payload.message },
            }));
            setActive((current) => (current === payload.step ? null : current));
            if (status === 'fail' && payload.message) push(payload.message, 'err');
          }
          break;
        case 'log':
          push(payload.text ?? '', payload.stream === 'stderr' ? 'err' : 'plain');
          break;
        case 'warn':
          if (payload.text) {
            setWarnings((current) => (current.includes(payload.text!) ? current : [...current, payload.text!]));
            push(payload.text, 'warn');
          }
          break;
        case 'device.leased':
          push(`emulator ${payload.avd} on port ${payload.port}`, 'note');
          break;
        case 'job.requeued':
          push('worker stopped reporting; job returned to the queue', 'warn');
          break;
        case 'job.cancelled':
          push('build cancelled', 'warn');
          break;
        case 'artifact':
        case 'meta':
        case 'job.start':
        case 'job.claimed':
          break;
        case 'job.finished':
          void api.job(jobId).then((value) => {
            if (!cancelled) setJob(value);
          });
          break;
        default:
          break;
      }
    };

    const close = streamJob(jobId, onEvent, () => {
      void api.job(jobId).then((value) => {
        if (!cancelled) setJob(value);
      });
    });

    return () => {
      cancelled = true;
      close();
    };
  }, [jobId, nonce]);

  return { job, steps, active, lines, warnings, refresh: () => setNonce((n) => n + 1) };
}
